import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, Loader2, X, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

function TierModal({ tier, onClose, onSave }) {
  const isEdit = !!tier;
  const [form, setForm] = useState({
    name: tier?.name || '',
    description: tier?.description || '',
    price_usd: tier?.price_usd ?? '',
    duration_days: tier?.duration_days ?? '',
    sort_order: tier?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.price_usd || parseFloat(form.price_usd) <= 0) { toast.error('Price must be greater than 0'); return; }
    if (!form.duration_days || parseInt(form.duration_days) <= 0) { toast.error('Duration must be at least 1 day'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price_usd: parseFloat(form.price_usd),
        duration_days: parseInt(form.duration_days),
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (isEdit) {
        await adminAPI.updatePromotionTier(tier.id, payload);
        toast.success('Tier updated');
      } else {
        await adminAPI.createPromotionTier(payload);
        toast.success('Tier created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="tier-modal-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden" data-testid="tier-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <h2 className="font-bold text-zinc-900">{isEdit ? 'Edit Tier' : 'Add Tier'}</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors" data-testid="tier-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Name *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
              placeholder="e.g. Featured Ad" data-testid="tier-form-name"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none resize-none"
              rows={2} placeholder="Optional description" data-testid="tier-form-description"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Price (USD) *</label>
              <input type="number" step="0.01" min="0.01" value={form.price_usd} onChange={e => setForm(f => ({ ...f, price_usd: e.target.value }))}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                placeholder="9.99" data-testid="tier-form-price"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Duration (days) *</label>
              <input type="number" min="1" value={form.duration_days} onChange={e => setForm(f => ({ ...f, duration_days: e.target.value }))}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                placeholder="7" data-testid="tier-form-duration"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Sort Order</label>
              <input type="number" min="0" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                placeholder="0" data-testid="tier-form-sort-order"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
              data-testid="tier-form-cancel"
            >Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002585] transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="tier-form-submit"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Tier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteDialog({ tierName, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-dialog-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6" data-testid="delete-dialog">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-zinc-900">Deactivate Tier</h3>
            <p className="text-sm text-zinc-500">This will hide <strong>{tierName}</strong> from sellers.</p>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mb-5">Existing promotions using this tier will continue to work. The tier can be reactivated later.</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors" data-testid="delete-dialog-cancel">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={deleting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="delete-dialog-confirm"
          >
            {deleting && <Loader2 className="w-3 h-3 animate-spin" />}
            Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPromotionTiers() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalTier, setModalTier] = useState(undefined); // undefined=closed, null=create, object=edit
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchTiers = async () => {
    try {
      const { data } = await adminAPI.getPromotionTiers();
      setTiers(data);
    } catch (err) {
      toast.error('Failed to load promotion tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTiers(); }, []);

  const handleDelete = async () => {
    try {
      await adminAPI.deletePromotionTier(deleteTarget.id);
      toast.success('Tier deactivated');
      setDeleteTarget(null);
      fetchTiers();
    } catch (err) {
      toast.error('Failed to deactivate tier');
    }
  };

  const handleReactivate = async (tier) => {
    try {
      await adminAPI.updatePromotionTier(tier.id, { is_active: true });
      toast.success('Tier reactivated');
      fetchTiers();
    } catch (err) {
      toast.error('Failed to reactivate tier');
    }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="admin-tiers-loading">
      {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-lg border border-zinc-200 p-6 animate-pulse"><div className="h-5 bg-zinc-100 rounded w-1/3 mb-4" /><div className="h-10 bg-zinc-100 rounded" /></div>)}
    </div>
  );

  const activeTiers = tiers.filter(t => t.is_active);
  const inactiveTiers = tiers.filter(t => !t.is_active);

  return (
    <div data-testid="admin-promotion-tiers">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Promotion Tiers</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage pricing packages for listing promotions</p>
        </div>
        <button onClick={() => setModalTier(null)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002585] transition-colors"
          data-testid="add-tier-btn"
        >
          <Plus className="w-4 h-4" /> Add Tier
        </button>
      </div>

      {/* Active Tiers Table */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden mb-6" data-testid="active-tiers-table">
        <div className="px-6 py-4 border-b border-zinc-100">
          <h2 className="font-bold text-zinc-900 text-sm">Active Tiers ({activeTiers.length})</h2>
        </div>
        {activeTiers.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-zinc-400">No active tiers. Add one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Price</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Duration</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Sort</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeTiers.map(tier => (
                  <tr key={tier.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors" data-testid={`tier-row-${tier.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-zinc-900">{tier.name}</p>
                      {tier.description && <p className="text-xs text-zinc-400 mt-0.5 max-w-xs truncate">{tier.description}</p>}
                    </td>
                    <td className="px-6 py-4 font-mono font-semibold text-zinc-900">${tier.price_usd?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-zinc-600">{tier.duration_days} day{tier.duration_days !== 1 ? 's' : ''}</td>
                    <td className="px-6 py-4 text-zinc-400">{tier.sort_order}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                        <Check className="w-3 h-3" /> Active
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setModalTier(tier)}
                          className="p-2 rounded-lg text-zinc-400 hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
                          title="Edit" data-testid={`edit-tier-${tier.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(tier)}
                          className="p-2 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Deactivate" data-testid={`delete-tier-${tier.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inactive Tiers */}
      {inactiveTiers.length > 0 && (
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid="inactive-tiers-table">
          <div className="px-6 py-4 border-b border-zinc-100">
            <h2 className="font-bold text-zinc-500 text-sm">Inactive Tiers ({inactiveTiers.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Price</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Duration</th>
                  <th className="text-right px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inactiveTiers.map(tier => (
                  <tr key={tier.id} className="border-b border-zinc-50 opacity-60" data-testid={`tier-row-inactive-${tier.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-zinc-600">{tier.name}</p>
                    </td>
                    <td className="px-6 py-4 font-mono text-zinc-500">${tier.price_usd?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-zinc-500">{tier.duration_days} day{tier.duration_days !== 1 ? 's' : ''}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleReactivate(tier)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#002FA7] border border-[#002FA7]/20 hover:bg-[#002FA7]/5 transition-colors"
                        data-testid={`reactivate-tier-${tier.id}`}
                      >
                        Reactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalTier !== undefined && (
        <TierModal
          tier={modalTier}
          onClose={() => setModalTier(undefined)}
          onSave={() => { setModalTier(undefined); fetchTiers(); }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <DeleteDialog
          tierName={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
